<template>
  <a
    href="#main-content"
    class="sr-only focus:not-sr-only focus:absolute focus:z-[9999999999] focus:p-2 focus:bg-white focus:text-black focus:rounded"
  >
    Skip to main content
  </a>
  <RouterView />
  <div :id="portalRootElementId">
    <!-- Use this element as the root for all portals -->
  </div>

  <Teleport :to="portalRootElementSelector">
    <div
      v-if="!appStore.isSimulfest"
      class="toast toast-center toast-top z-[999999999] w-full max-w-sm px-0"
      :class="{
        'pointer-events-none': toasts.size === 0,
      }"
    >
      <Toast
        v-for="[id, toast] in toasts"
        :key="id"
        :message="toast.message"
        :type="toast.type"
        :title="toast.title"
        :persistent="toast.persistent"
        :closable="toast.closable"
        :icon="toast.icon"
        :action="toast.action"
        @close="() => removeToast(id)"
      />
    </div>
  </Teleport>

  <CookieConsentBanner
    v-if="!appStore.isSimulfest && !appStore.isEmbed && !isEmbeddedAuthRoute"
  />
  <CookieSettingsModal v-if="appStore.showCookieSettings" />
  <EmailConsentPrompt v-if="!appStore.isSimulfest" />

  <!-- Service Worker Update Prompt -->
  <ServiceWorkerUpdatePrompt
    v-if="!appStore.isSimulfest"
    :need-refresh="swNeedRefresh"
    :offline-ready="swOfflineReady"
    :update-service-worker="swUpdateServiceWorker"
    :is-updating="swIsUpdating"
    :close="swClose"
  />
</template>

<script setup lang="ts">
import { RouterView, useRoute } from "vue-router";
import { portalRootElementId, portalRootElementSelector } from "./constants/ui";
import { useProvideResponsiveHelpers } from "./composables/global/use-responsive-helpers";
import { useAppStore } from "./stores/app.store";
import { useProvideActivationContainer } from "./composables/global/use-activation-dependent-action";
import { useThemeStore } from "./stores/theme.store";
import { computed, watchEffect, watch } from "vue";
import { RouteNames } from "./router/app.router";
import Toast from "./components/ui/toast/toast.vue";
import { useToastStore } from "./stores/toast.store";
import CookieConsentBanner from "./components/gdpr/cookie-consent-banner.vue";
import { useSetupHistory } from "./composables/use-history";
import CookieSettingsModal from "./components/gdpr/cookie-settings-modal.vue";
import EmailConsentPrompt from "./components/gdpr/email-consent-prompt.vue";
import { useMutation } from "@vue/apollo-composable";
import { graphql as gql } from "@/generated";
import { useUserStore } from "./stores/user.store";
import { useProvideOnlineNow } from "./composables/global/use-online-now";
import { useProvideRecentAvatars } from "./composables/global/use-recent-avatars";
import ServiceWorkerUpdatePrompt from "./components/service-worker-update-prompt.vue";
import { useServiceWorker } from "./composables/use-service-worker";
import { useIntercomService } from "./stores/tracking/use-intercom-service";
import { usePrivacySignals } from "./composables/use-privacy-signals";

const SessionStartDocument = gql(/* GraphQL */ `
  mutation SessionStart {
    sessionStart
  }
`);

useProvideResponsiveHelpers();
useProvideActivationContainer();
useSetupHistory();

const appStore = useAppStore();
const route = useRoute();

const isEmbeddedAuthRoute = computed(
  () => route.name === RouteNames.ChannelAuth,
);

useProvideOnlineNow();
useProvideRecentAvatars();
usePrivacySignals();

const themeStore = useThemeStore();
const userStore = useUserStore();
const { toasts, removeToast } = useToastStore();

const { mutate: sessionStart } = useMutation(SessionStartDocument);

// Service Worker setup
const {
  needRefresh: swNeedRefresh,
  offlineReady: swOfflineReady,
  updateServiceWorker: swUpdateServiceWorker,
  isUpdating: swIsUpdating,
  close: swClose,
} = useServiceWorker();

const stopWatcher = watch(
  () => userStore.isAuthenticated,
  (isAuthenticated) => {
    if (isAuthenticated) {
      sessionStart();
      stopWatcher();
    }
  },
);

// Update document body with theme colors
watchEffect((onCleanup) => {
  const theme = themeStore.currentThemeData;

  if (!theme) return;

  const cssVariables = {
    "--brand-bg-main": theme.brand_bg_main,
    "--brand-light-bg-overlay-50": theme.brand_strong_bg_overlay_80,
    "--brand-strong-bg-overlay-80": theme.brand_light_bg_overlay_50,
    "--brand-primary": theme.brand_primary,
    "--brand-surface-hover": theme.brand_surface_hover,
    "--brand-bgdivider-20": theme.brand_bgdivider_10,
    "--brand-bgdivider-10": theme.brand_bgdivider_20,
    "--brand-quaternary-40": theme.brand_quaternary_40,
    "--brand-secondary-80": theme.brand_secondary_80,
    "--brand-tertiary-60": theme.brand_tertiary_60,
    "--brand-fill-dark": theme.brand_fill_dark,
    "--brand-icon-emphasis": theme.brand_icon_emphasis,

    // New design tokens
    "--bg": theme.bg,
    "--bg-secondary": theme.bg_secondary,
    "--surface": theme.surface,
    "--surface-hover": theme.surface_hover,
    "--surface-secondary": theme.surface_secondary,
    "--surface-secondary-hover": theme.surface_secondary_hover,
    "--surface-accent": theme.surface_accent,
    "--fill-brand": theme.fill_brand,
    "--fill-brand-hover": theme.fill_brand_hover,
    "--fill-brand-pressed": theme.fill_brand_pressed,
    "--fill-brand-secondary": theme.fill_brand_secondary,
    "--fill-brand-secondary-hover": theme.fill_brand_secondary_hover,
    "--fill-brand-secondary-pressed": theme.fill_brand_secondary_pressed,
    "--fill-dark": theme.fill_dark,
    "--fill-dark-hover": theme.fill_dark_hover,
    "--fill-dark-pressed": theme.fill_dark_pressed,
    "--fill-disabled": theme.fill_disabled,
    "--fill-success": theme.fill_success,
    "--fill-critical": theme.fill_critical,
    "--fill-critical-secondary": theme.fill_critical_secondary,
    "--fill-warning": theme.fill_warning,
    "--fill-warning-secondary": theme.fill_warning_secondary,
    "--fill-silver": theme.fill_silver,
    "--fill-bronze": theme.fill_bronze,
    "--fill-gold": theme.fill_gold,
    "--fill-gold-light": theme.fill_gold_light,
    "--fill-gold-dark": theme.fill_gold_dark,
    "--text": theme.text,
    "--text-on-brand": theme.text_on_brand,
    "--text-inverse": theme.text_inverse,
    "--text-secondary": theme.text_secondary,
    "--text-tertiary": theme.text_tertiary,
    "--text-disabled": theme.text_disabled,
    "--text-success": theme.text_success,
    "--text-critical": theme.text_critical,
    "--text-brand": theme.text_brand,
    "--text-accent": theme.text_accent,
    "--text-accent-secondary": theme.text_accent_secondary,
    "--text-gold": theme.text_gold,
    "--text-gold-light": theme.text_gold_light,
    "--text-gold-dark": theme.text_gold_dark,
    "--border": theme.border,
    "--border-hover": theme.border_hover,
    "--border-pressed": theme.border_pressed,
    "--border-selected": theme.border_selected,
    "--border-secondary": theme.border_secondary,
    "--border-brand": theme.border_brand,
    "--border-brand-hover": theme.border_brand_hover,
    "--border-brand-pressed": theme.border_brand_pressed,
    "--border-brand-selected": theme.border_brand_selected,
    "--border-critical": theme.border_critical,
    "--border-success": theme.border_success,
    "--border-warning": theme.border_warning,
    "--border-dark": theme.border_dark,
    "--border-button-shadow-light": theme.border_button_shadow_light,
    "--border-button-shadow-dark": theme.border_button_shadow_dark,
    "--icon": theme.icon,
    "--icon-emphasis": theme.icon_emphasis,
    "--icon-on-branded-surface": theme.icon_on_branded_surface,
    "--icon-hover": theme.icon_hover,
    "--icon-secondary": theme.icon_secondary,
    "--icon-secondary-hover": theme.icon_secondary_hover,
    "--icon-disabled": theme.icon_disabled,
    "--icon-brand": theme.icon_brand,
  };

  for (const [key, value] of Object.entries(cssVariables)) {
    if (value) {
      document.body.style.setProperty(key, value);
    }
  }

  onCleanup(() => {
    for (const key of Object.keys(cssVariables)) {
      document.body.style.removeProperty(key);
    }
  });
});

const intercomService = useIntercomService();

watchEffect(() => {
  if (!themeStore.currentThemeData || !intercomService.value) return;

  if (!intercomService.value.initialized) return;

  if (intercomService.value) {
    intercomService.value.updateTheme(themeStore.currentThemeData);
    console.log("[App] Updated Intercom theme");
  } else {
    console.error("[App] Unable to update Intercom theme, no service found");
  }
});
</script>

<style scoped></style>
